import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CalendarView = ({ events, onEventClick, currentDate = new Date() }) => {
  const [viewDate, setViewDate] = useState(currentDate);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    
    const dateString = date?.toISOString()?.split('T')?.[0];
    return events?.filter(event => {
      const eventDate = new Date(event.dateTime)?.toISOString()?.split('T')?.[0];
      return eventDate === dateString;
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(viewDate);
    newDate?.setMonth(viewDate?.getMonth() + direction);
    setViewDate(newDate);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    return date < today;
  };

  const days = getDaysInMonth(viewDate);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth(-1)}
          iconName="ChevronLeft"
        />
        
        <div className="flex items-center space-x-4">
          <h3 className="font-heading font-semibold text-lg text-foreground">
            {monthNames?.[viewDate?.getMonth()]} {viewDate?.getFullYear()}
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewDate(new Date())}
          >
            Today
          </Button>
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigateMonth(1)}
          iconName="ChevronRight"
        />
      </div>
      {/* Days of Week Header */}
      <div className="grid grid-cols-7 border-b border-border">
        {daysOfWeek?.map((day) => (
          <div key={day} className="p-3 text-center border-r border-border last:border-r-0">
            <span className="text-sm font-medium text-muted-foreground">
              {day}
            </span>
          </div>
        ))}
      </div>
      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {days?.map((date, index) => {
          const dayEvents = getEventsForDate(date);
          const hasEvents = dayEvents?.length > 0;
          
          return (
            <div
              key={index}
              className={`min-h-[120px] p-2 border-r border-b border-border last:border-r-0 ${
                !date ? 'bg-muted/20' : ''
              } ${isToday(date) ? 'bg-primary/5' : ''} ${
                isPastDate(date) ? 'opacity-60' : ''
              }`}
            >
              {date && (
                <>
                  {/* Date Number */}
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${
                      isToday(date) ? 'text-primary' : 'text-foreground'
                    }`}>
                      {date?.getDate()}
                    </span>
                    {hasEvents && (
                      <div className="w-2 h-2 bg-accent rounded-full animate-breathing"></div>
                    )}
                  </div>

                  {/* Events */}
                  <div className="space-y-1">
                    {dayEvents?.slice(0, 3)?.map((event, eventIndex) => (
                      <button
                        key={eventIndex}
                        onClick={() => onEventClick(event)}
                        className="w-full text-left p-1 rounded text-xs bg-primary/10 text-primary hover:bg-primary/20 transition-wellness truncate"
                      >
                        <div className="flex items-center space-x-1">
                          <Icon 
                            name={event?.type === 'Virtual' ? 'Video' : 'MapPin'} 
                            size={10} 
                          />
                          <span className="truncate">{event?.title}</span>
                        </div>
                        <div className="text-xs opacity-75 mt-0.5">
                          {new Date(event.dateTime)?.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                      </button>
                    ))}
                    
                    {dayEvents?.length > 3 && (
                      <button
                        onClick={() => onEventClick({ date, events: dayEvents })}
                        className="w-full text-left p-1 rounded text-xs text-muted-foreground hover:text-foreground transition-wellness"
                      >
                        +{dayEvents?.length - 3} more
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      {/* Calendar Legend */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary/10 rounded"></div>
            <span>Events Available</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-accent rounded-full animate-breathing"></div>
            <span>Has Events</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-primary/5 border border-primary rounded"></div>
            <span>Today</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;